import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BlobProvider } from "@react-pdf/renderer";
import {
  IoSearch,
  IoDocumentText,
  IoClose,
  IoExpand,
  IoGrid,
  IoList,
} from "react-icons/io5";
import { GrPowerReset } from "react-icons/gr";
import { HiDownload } from "react-icons/hi";

import { Loader, ProductPdf, Navbar, ScrollToTop } from "../components";
import { createSlugForTableName } from "../helpers/inputSanitation";
import { useCatalogContext } from "../contexts/CatalogContextProvider";
import { tableConfig } from "../config/tableConfig";

const Products = () => {
  const { category } = useParams();
  const navigate = useNavigate();

  const {
    getSelectedTableData,
    selectedTableData,
    selectedTable,
    isLoading,
    getBrandInfo,
    getAllActiveTables,
    brandInfo,
    activeTables,
    setSelectedTable,
  } = useCatalogContext();

  const [isCurrentDeviceMobile, setIsCurrentDeviceMobile] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [searchItem, setSearchItem] = useState("");
  const [tableNotFound, setTableNotFound] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const pdfViewWidth = "90vw";
  const pdfViewHeight = "90vh";

  // Function to find table by category slug
  const findTableByCategory = (categorySlug, tables) => {
    if (!tables?.tables) return null;

    return tables.tables.find(
      (table) => createSlugForTableName(table.tableName) === categorySlug
    );
  };

  const handleOpenPdfModal = () => {
    if (isCurrentDeviceMobile) {
      alert(
        "PDF generation is not supported on mobile devices. Please open this web page on Desktop, Laptop or PC to download PDF."
      );
      return;
    }
    setPdfModalOpen(true);
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    setIsPdfGenerating(false);
  };

  const handleImageClick = (imageUrl, productName) => {
    setFullscreenImage({ url: imageUrl, name: productName });
  };

  // Filter products based on search
  const filteredProducts =
    selectedTableData?.records?.filter((element) => {
      if (!searchItem) return true;

      const searchTerm = searchItem.toLowerCase();
      const category = element?.fields?.category?.toLowerCase() || "";
      const description = element?.fields?.description?.toLowerCase() || "";
      const partNo = element?.fields?.part_no?.toLowerCase() || "";

      return (
        category.includes(searchTerm) ||
        description.includes(searchTerm) ||
        partNo.includes(searchTerm)
      );
    }) || [];

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android|iPad|iPhone|iPod/i.test(userAgent)) {
      setIsCurrentDeviceMobile(true);
    } else {
      setIsCurrentDeviceMobile(false);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      if (!activeTables?.tables) {
        await getAllActiveTables();
        return;
      }

      await getBrandInfo();

      const matchingTable = findTableByCategory(category, activeTables);

      if (matchingTable) {
        setSelectedTable(matchingTable);
        getSelectedTableData(matchingTable.tableName);
        setTableNotFound(false);
      } else {
        setTableNotFound(true);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, activeTables]);

  // Loading state
  if (!activeTables?.tables && !tableNotFound) {
    return (
      <div className="min-h-screen max-w-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 py-20">
          <div className="flex flex-col justify-center items-center gap-6 text-center">
            <Loader />
            <h3 className="text-xl text-gray-600">
              Loading product catalog...
            </h3>
          </div>
        </div>
      </div>
    );
  }

  // Table not found state
  if (tableNotFound) {
    return (
      <div className="min-h-screen max-w-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <Navbar />
        <div className="container mx-auto px-4 md:px-6 py-20">
          <div className="flex flex-col justify-center items-center gap-6 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-red-400 to-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-3xl">!</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">
              Category Not Found
            </h2>
            <p className="text-gray-600 text-lg">
              The category "{category}" does not exist in our catalog.
            </p>
            <button onClick={() => navigate("/")} className="my-button-1">
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      <Navbar />
      <div className="container mx-auto px-4 md:px-6 py-8">
        {isLoading || !selectedTableData?.records ? (
          <div className="flex flex-col justify-center items-center gap-6 text-center py-20">
            <Loader />
            <h3 className="text-xl text-gray-600">
              Generating "{selectedTable?.displayName || category}" Catalog
            </h3>
          </div>
        ) : (
          selectedTableData?.records?.length > 0 && (
            <div className="space-y-8">
              {/* Header Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                  {/* Category Info */}
                  <div className="lg:col-span-2 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Product Category
                      </p>
                      <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-themeColor1 to-themeColor2 text-transparent bg-clip-text">
                        {selectedTable?.displayName}
                      </h1>
                    </div>

                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Total Products
                        </p>
                        <p className="text-2xl font-bold text-gray-800">
                          {selectedTableData?.records?.length}
                        </p>
                      </div>

                      <div className="h-12 w-px bg-gray-200"></div>

                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Search Results
                        </p>
                        <p className="text-2xl font-bold text-themeColor1">
                          {filteredProducts.length}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* PDF Download Section */}
                  <div className="bg-gradient-to-br from-themeColor1/5 to-themeColor2/5 rounded-xl p-6 border border-themeColor1/20">
                    <div className="text-center space-y-3">
                      <IoDocumentText className="text-3xl text-themeColor1 mx-auto" />
                      <h3 className="font-semibold text-gray-800">
                        Export Catalog
                      </h3>
                      <p className="text-sm text-gray-600">
                        Generate a PDF of all products
                      </p>
                      <button
                        className={`w-full bg-gradient-to-r from-themeColor1 to-themeColor2 hover:from-themeColor2 hover:to-themeColor1 text-white px-4 py-3 font-semibold rounded-lg my-smooth-transition-1 flex items-center justify-center gap-2 ${
                          isCurrentDeviceMobile
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:shadow-lg transform hover:-translate-y-0.5"
                        }`}
                        onClick={handleOpenPdfModal}
                        disabled={isCurrentDeviceMobile}
                      >
                        <HiDownload className="text-lg" />
                        {isCurrentDeviceMobile
                          ? "Desktop Only"
                          : "Download PDF"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and View Controls */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sticky top-0 z-20">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  {/* Search Bar */}
                  <div className="relative flex-1 w-full lg:max-w-lg">
                    <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                    <input
                      type="text"
                      placeholder="Search by product name, part number, or category..."
                      value={searchItem}
                      onChange={(e) => setSearchItem(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 hover:border-themeColor1/50 focus:border-themeColor1 rounded-lg outline-none my-smooth-transition-1 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  {/* Toggles and Reset Buttons */}
                  <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setSearchItem("")}
                      className={`p-2 rounded-md my-smooth-transition-1 ${
                        searchItem !== ""
                          ? "bg-white text-themeColor1 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <GrPowerReset className="text-lg" />
                    </button>
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-md my-smooth-transition-1 ${
                        viewMode === "grid"
                          ? "bg-white text-themeColor1 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <IoGrid className="text-lg" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-md my-smooth-transition-1 ${
                        viewMode === "list"
                          ? "bg-white text-themeColor1 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <IoList className="text-lg" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Products Grid/List */}
              <div
                className={`${
                  viewMode === "grid"
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6"
                    : "space-y-4"
                }`}
              >
                {filteredProducts.map((element, key) =>
                  viewMode === "grid" ? (
                    // Grid View Card
                    <div
                      key={key}
                      className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-themeColor1/30 overflow-hidden my-smooth-transition-1 group"
                    >
                      {/* Product Image */}
                      <div
                        className="aspect-square bg-gray-50 overflow-hidden cursor-pointer relative group"
                        onClick={() =>
                          handleImageClick(
                            element?.fields?.images?.[
                              tableConfig.userInfo.defaultImgIndexToShow
                            ]?.url,
                            element?.fields?.description
                          )
                        }
                      >
                        <img
                          src={
                            element?.fields?.images?.[
                              tableConfig.userInfo.defaultImgIndexToShow
                            ]?.url
                          }
                          alt={element?.fields?.description}
                          className="w-full h-full object-cover group-hover:scale-105 my-smooth-transition-1"
                        />
                        {/* Expand Icon Overlay */}
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 my-smooth-transition-1 flex items-center justify-center">
                          <div className="bg-white/90 p-2 rounded-full shadow-lg">
                            <IoExpand className="text-themeColor1 text-xl" />
                          </div>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="p-6 space-y-3">
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-themeColor1 uppercase tracking-wide">
                            {element?.fields?.part_no}
                          </p>
                          <h3 className="text-lg font-semibold text-gray-800 group-hover:text-themeColor1 my-smooth-transition-1">
                            {element?.fields?.description}
                          </h3>
                        </div>

                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-sm text-gray-600 font-medium">
                            Category: {element?.fields?.category}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // List View Card
                    <div
                      key={key}
                      className="bg-white rounded-xl shadow-sm hover:shadow-md border border-gray-200 hover:border-themeColor1/30 p-6 my-smooth-transition-1 group"
                    >
                      <div className="flex items-center gap-6">
                        {/* Product Image */}
                        <div
                          className="w-48 h-48 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer relative group"
                          onClick={() =>
                            handleImageClick(
                              element?.fields?.images?.[
                                tableConfig.userInfo.defaultImgIndexToShow
                              ]?.url,
                              element?.fields?.description
                            )
                          }
                        >
                          <img
                            src={
                              element?.fields?.images?.[
                                tableConfig.userInfo.defaultImgIndexToShow
                              ]?.url
                            }
                            alt={element?.fields?.description}
                            className="w-full h-full object-cover group-hover:scale-105 my-smooth-transition-1"
                          />
                          {/* Expand Icon Overlay */}
                          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 my-smooth-transition-1 flex items-center justify-center">
                            <div className="bg-white/90 p-2 rounded-full shadow-lg">
                              <IoExpand className="text-themeColor1 text-xl" />
                            </div>
                          </div>
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 space-y-2">
                          <div>
                            <p className="text-sm font-medium text-themeColor1 uppercase tracking-wide">
                              {element?.fields?.part_no}
                            </p>
                            <h3 className="text-xl font-semibold text-gray-800 group-hover:text-themeColor1 my-smooth-transition-1">
                              {element?.fields?.description}
                            </h3>
                          </div>
                          <p className="text-gray-600">
                            <span className="font-medium">Category:</span>{" "}
                            {element?.fields?.category}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* No Results State */}
              {filteredProducts.length === 0 && searchItem && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IoSearch className="text-3xl text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    No products found
                  </h3>
                  <p className="text-gray-600">
                    Try adjusting your search terms or browse all products.
                  </p>
                  <button
                    onClick={() => setSearchItem("")}
                    className="mt-4 text-themeColor1 hover:text-themeColor2 font-medium my-smooth-transition-1"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          )
        )}
      </div>

      {/* {console.log("selectedTable", selectedTable)} */}

      {/* Fullscreen Image Modal (add this at the end of your component, before closing div) */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-7xl max-h-full w-full">
            {/* Close Button */}
            <button
              onClick={() => setFullscreenImage(null)}
              className="absolute -top-12 right-0 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full my-smooth-transition-1 z-10"
            >
              <IoClose className="text-2xl" />
            </button>

            {/* Product Info Header */}
            <div className="absolute -top-12 left-0 text-white mb-4">
              <h3 className="text-lg font-semibold">{fullscreenImage.name}</h3>
            </div>

            {/* Fullscreen Image */}
            <div
              className="bg-white rounded-lg overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fullscreenImage.url}
                alt={fullscreenImage.name}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
      {/* PDF Modal */}
      {!isCurrentDeviceMobile && pdfModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl relative overflow-hidden"
            style={{ width: pdfViewWidth, height: pdfViewHeight }}
          >
            {/* Close Button */}
            <button
              onClick={handleClosePdfModal}
              className="absolute top-4 right-4 z-50 bg-white/90 hover:bg-white text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-lg my-smooth-transition-1"
            >
              <IoClose className="text-xl" />
            </button>

            {/* {console.log("brandInfo", brandInfo)} */}

            <BlobProvider
              document={
                <ProductPdf
                  selectedTable={selectedTable}
                  brandInfo={brandInfo?.records}
                  catalogData={selectedTableData?.records}
                />
              }
            >
              {({ url, loading, error }) => {
                if (loading) {
                  return (
                    <div className="flex items-center justify-center h-full flex-col gap-4">
                      <div className="w-12 h-12 border-4 border-gray-200 border-t-themeColor1 rounded-full animate-spin"></div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        Generating PDF...
                      </h3>
                      <p className="text-gray-600">
                        Please wait, this may take up to 1 minute
                      </p>
                    </div>
                  );
                }

                if (error) {
                  return (
                    <div className="flex items-center justify-center h-full flex-col gap-4 text-center">
                      <div className="text-red-500 text-4xl">!</div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        Error generating PDF
                      </h3>
                      <p className="text-gray-600">{error.message}</p>
                    </div>
                  );
                }

                return (
                  <iframe
                    src={url}
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: "16px" }}
                    title="PDF Viewer"
                  />
                );
              }}
            </BlobProvider>
          </div>
        </div>
      )}
      <ScrollToTop />
    </div>
  );
};

export default Products;
